/**
 * Calls the appropriate underlying mutation to advance the property's stage.
 *
 * Stage transitions are side-effects on existing entities (Product /
 * LoanApplication / Contract / pipeline) — see plan §1.5.
 */

import { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_QUALIFY_STATUS } from '@/config/constants';
import {
  useCreateLoanApplicationMutation,
  useGetMyLoanApplicationsQuery,
  useGetOrCreatePurchaseLoanApplicationMutation,
} from '@/apis/extended/loanApplicationApi';
import { useStartContractMutation } from '@/apis/extended/productApi';
import { useUpdateUserMutation } from '@/apis/extended/userApi';
import { rtkApi } from '@/apis/rtkApi';
import {
  nextStepLabel,
  stageRequirements,
} from '@/components/Property/propertyCardConstants';
import { isCashPurchaseProperty } from '@/utils/cashPurchase';
import { closedAtStore } from '@/utils/propertyView/localStateStore';
import type {
  LoanProductType,
  LoanSourceProductSummary,
} from '@/types/LoanApplicationType';
import type { PropertyStage, PropertyView } from '@/types/PropertyView';

function linkedProductId(
  ref: string | LoanSourceProductSummary | null | undefined
): string | undefined {
  if (ref == null) return undefined;
  if (typeof ref === 'string') return ref;
  return ref._id;
}

function productTypeForNextStep(property: PropertyView): LoanProductType {
  if (property.isDscr) return 'dscr';
  switch (property.loanProgram) {
    case 'fha':
      return 'fha';
    case 'va':
      return 'va';
    case 'usda':
      return 'usda';
    case 'bridge':
      return 'bridge';
    default:
      return 'conventional';
  }
}

interface NextStepCTAProps {
  property: PropertyView;
  currentStage: number;
}

export const NextStepCTA = ({ property, currentStage }: NextStepCTAProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const requirement = stageRequirements(property);
  const isCurrentStage = currentStage === property.stage;
  const disabled = !isCurrentStage || !requirement.satisfied;

  const [updateUser] = useUpdateUserMutation();
  const [createApp] = useCreateLoanApplicationMutation();
  const [getOrCreatePurchase] = useGetOrCreatePurchaseLoanApplicationMutation();
  const [startContract] = useStartContractMutation();
  const { data: applications } = useGetMyLoanApplicationsQuery();
  const [loading, setLoading] = useState(false);

  const handleAdvance = async () => {
    if (disabled) return;
    setLoading(true);
    try {
      if (property.pipelineMode === 'seller') {
        if (property.stage >= 7) {
          closedAtStore.set(property.id, new Date().toISOString());
          toast.success('Sale marked complete');
          return;
        }
        const next = Math.min(property.stage + 1, 7) as PropertyStage;
        navigate(`/properties/${property.id}/stage/${next}`);
        return;
      }

      switch (property.stage) {
        case 1: {
          // 1 → 2: ensure user.qualifyStatus = qualify.
          await updateUser({ qualifyStatus: USER_QUALIFY_STATUS.QUALIFY })
            .unwrap()
            .catch(() => undefined);
          toast.success('Pre-qualification updated');
          navigate(`/properties/${property.id}/stage/2`);
          break;
        }
        case 2: {
          // Cash path: skip financed 1003 — Stage 3 is proof-of-funds only.
          if (isCashPurchaseProperty(property)) {
            toast.success('Cash path — no mortgage application required');
            navigate(`/properties/${property.id}/stage/3`);
            break;
          }
          // 2 → 3: ensure a loan application exists for this property.
          if (!property.applicationId) {
            try {
              if (property.isRefinance) {
                const flowProfile = 'refinance';
                const productType = productTypeForNextStep(property);
                const appForThisProperty = applications?.find(
                  (a) =>
                    linkedProductId(a.sourceProductId) === property.id ||
                    linkedProductId(a.focusProductId) === property.id
                );
                if (appForThisProperty) {
                  toast.success('Application already linked to this property');
                } else {
                  await createApp({
                    sourceProductId: property.id,
                    focusProductId: property.id,
                    flowProfile,
                    productType,
                  }).unwrap();
                  toast.success('Application started');
                }
              } else {
                const appForThisProperty = applications?.find(
                  (a) =>
                    linkedProductId(a.sourceProductId) === property.id ||
                    linkedProductId(a.focusProductId) === property.id
                );
                if (appForThisProperty) {
                  toast.success('Application already linked to this property');
                } else {
                  await getOrCreatePurchase({
                    flowProfile: property.isDscr ? 'investor_dscr' : 'purchase',
                    focusProductId: property.id,
                    sourceProductId: property.id,
                    productType: productTypeForNextStep(property),
                    approvalChannel: 'deal_room_next_step',
                    investorModeAtCreation: property.isDscr,
                  }).unwrap();
                  toast.success('Application started');
                }
              }
            } catch {
              toast.error('Could not start application');
            }
          }
          navigate(`/properties/${property.id}/stage/3`);
          break;
        }
        case 3: {
          if (property.isRefinance) {
            navigate(`/properties/${property.id}/stage/7`);
          } else {
            navigate(`/properties/${property.id}/stage/4`);
          }
          break;
        }
        case 4: {
          navigate(`/properties/${property.id}/stage/5`);
          break;
        }
        case 5: {
          // 4 → 5: ensure contract exists. If not, start it.
          if (!property.contractId) {
            try {
              await startContract(property.id).unwrap();
              toast.success('Offer wizard ready');
              // Re-fetch product to populate the new contract id.
              dispatch(
                rtkApi.util.invalidateTags([
                  { type: 'Product', id: property.id },
                ])
              );
            } catch {
              toast.error('Could not start contract');
            }
          }
          navigate(`/properties/${property.id}/stage/6`);
          break;
        }
        case 6: {
          navigate(`/properties/${property.id}/stage/7`);
          break;
        }
        case 7: {
          navigate(`/properties/${property.id}/stage/8`);
          break;
        }
        case 8: {
          navigate(`/properties/${property.id}/stage/9`);
          break;
        }
        case 9: {
          closedAtStore.set(property.id, new Date().toISOString());
          toast.success('Marked as closed — congrats!');
          dispatch(
            rtkApi.util.invalidateTags([{ type: 'Product', id: property.id }])
          );
          break;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const tooltip =
    disabled && requirement.missing.length > 0
      ? requirement.missing.join('\n')
      : '';

  const button = (
    <span style={{ width: isMobile ? '100%' : 'auto' }}>
      <Button
        variant="contained"
        fullWidth={isMobile}
        size="large"
        disabled={disabled || loading}
        onClick={handleAdvance}
        sx={{
          fontWeight: 700,
          textTransform: 'none',
          backgroundColor: property.isIdle
            ? 'var(--stage-idle)'
            : 'var(--primary-container)',
          minWidth: 200,
          '&:hover': {
            backgroundColor: property.isIdle ? '#c8851a' : '#0f2a5a',
          },
        }}
      >
        {loading ? (
          <CircularProgress size={20} sx={{ color: 'white' }} />
        ) : (
          nextStepLabel(property)
        )}
      </Button>
    </span>
  );

  const ctaControl = tooltip ? (
    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}>
      {button}
    </Tooltip>
  ) : (
    button
  );

  return (
    <Box
      sx={{
        position: isMobile ? 'fixed' : 'sticky',
        bottom: isMobile ? 0 : 24,
        left: 0,
        right: 0,
        py: 1.5,
        px: 2,
        display: 'flex',
        justifyContent: isMobile ? 'stretch' : 'flex-end',
        alignItems: 'center',
        backgroundColor: isMobile
          ? theme.palette.background.paper
          : 'transparent',
        borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
        zIndex: 10,
        // Full-width sticky bar is transparent on desktop but still
        // captured clicks on stage content (e.g. Stage 6 accordions).
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          width: isMobile ? '100%' : 'auto',
        }}
      >
        {ctaControl}
      </Box>
    </Box>
  );
};
